// Educational reference data for SentinelGuide. General information only — not legal advice.

export const ROADMAP_STAGES = [
  {
    id: "arrest",
    n: 1,
    title: "Arrest & Booking",
    icon: "Handcuffs",
    summary: "Police take you into custody. You are booked, and your constitutional rights kick in immediately.",
    prosecution: [
      "Officers document the alleged offense and gather initial evidence.",
      "You are fingerprinted, photographed, and personal property is inventoried.",
      "A prosecutor reviews the report to decide whether to file formal charges.",
    ],
    defense: [
      "Invoke your right to remain silent — say clearly: 'I am invoking my right to remain silent and I want a lawyer.'",
      "Do not answer questions or explain your side without counsel present.",
      "Write down everything you remember as soon as you can.",
    ],
    questions: [
      "What exactly am I being charged with?",
      "Was I read my Miranda rights, and when?",
      "How and when can I contact a lawyer?",
    ],
    risks: [
      "Saying too much — anything you say can be used against you.",
      "Consenting to searches you can legally decline.",
    ],
  },
  {
    id: "arraignment",
    n: 2,
    title: "Arraignment & Bail",
    icon: "Gavel",
    summary: "Your first court appearance. Charges are read, you enter a plea, and the judge decides bail/release.",
    prosecution: [
      "The formal charges are read into the record.",
      "The prosecutor may argue for higher bail or detention.",
    ],
    defense: [
      "Usually you (or your attorney) plead 'not guilty' to preserve all options.",
      "Your attorney argues for release on recognizance or reasonable bail.",
      "Bring proof of ties to the community (job, family, address).",
    ],
    questions: [
      "Should I plead not guilty today?",
      "What are the conditions of my release?",
      "What happens if I can't afford bail?",
    ],
    risks: [
      "Pleading guilty too early without understanding consequences.",
      "Violating release conditions, which can lead to re-arrest.",
    ],
  },
  {
    id: "discovery",
    n: 3,
    title: "Discovery & Investigation",
    icon: "FileSearch",
    summary: "Both sides exchange evidence. The prosecution must share material that could help your defense (Brady material).",
    prosecution: [
      "Turns over police reports, witness statements, lab results, and video.",
      "Must disclose exculpatory (favorable) evidence under Brady v. Maryland.",
    ],
    defense: [
      "Review every piece of evidence for weaknesses and gaps.",
      "Investigate witnesses and preserve favorable evidence.",
      "File motions to suppress illegally obtained evidence.",
    ],
    questions: [
      "What evidence does the prosecution actually have?",
      "Is there any evidence that helps me?",
      "Can any evidence be suppressed?",
    ],
    risks: [
      "Missing deadlines to file motions.",
      "Contacting witnesses directly, which can look like tampering.",
    ],
  },
  {
    id: "preliminary",
    n: 4,
    title: "Preliminary Hearing / Grand Jury",
    icon: "Scale",
    summary: "A judge or grand jury decides whether there is enough evidence (probable cause) to proceed to trial.",
    prosecution: [
      "Presents enough evidence to establish probable cause.",
      "In federal cases, a grand jury may issue an indictment.",
    ],
    defense: [
      "Test the strength of the prosecution's case and cross-examine witnesses.",
      "Sometimes charges get reduced or dismissed here.",
    ],
    questions: [
      "What's the difference between a preliminary hearing and a grand jury?",
      "Can charges be dropped at this stage?",
      "Should I testify? (Usually no.)",
    ],
    risks: [
      "Overexposing your defense strategy too early.",
    ],
  },
  {
    id: "plea",
    n: 5,
    title: "Plea Bargaining",
    icon: "Handshake",
    summary: "Most cases resolve through negotiation — pleading to a lesser charge or reduced sentence in exchange for avoiding trial.",
    prosecution: [
      "Offers a deal to secure a conviction without trial.",
      "May reduce charges or recommend a lighter sentence.",
    ],
    defense: [
      "Weigh the offer against the risk of trial with your attorney.",
      "Understand collateral consequences (immigration, jobs, gun rights).",
      "Never accept a plea you don't fully understand.",
    ],
    questions: [
      "What are the pros and cons of taking this deal?",
      "What's the realistic outcome if we go to trial instead?",
      "What are the collateral consequences of pleading guilty?",
    ],
    risks: [
      "Accepting a deal under pressure without understanding it.",
      "Unforeseen immigration or licensing consequences.",
    ],
  },
  {
    id: "trial",
    n: 6,
    title: "Trial",
    icon: "Users",
    summary: "If no plea is reached, the case goes to trial. The prosecution must prove guilt beyond a reasonable doubt.",
    prosecution: [
      "Presents evidence and witnesses to prove every element of the crime.",
      "Bears the burden of proof — beyond a reasonable doubt.",
    ],
    defense: [
      "You are presumed innocent and do not have to prove anything.",
      "Your attorney challenges evidence, cross-examines witnesses, and may present a defense.",
      "You have the right (but not obligation) to testify.",
    ],
    questions: [
      "Do I have to testify?",
      "What is our defense theory of the case?",
      "What does 'beyond a reasonable doubt' really mean?",
    ],
    risks: [
      "Reacting emotionally in front of the jury.",
      "Inconsistent testimony if you choose to testify.",
    ],
  },
  {
    id: "sentencing",
    n: 7,
    title: "Sentencing & Appeals",
    icon: "Milestone",
    summary: "If convicted, the judge imposes a sentence. You may have the right to appeal legal errors.",
    prosecution: [
      "Recommends a sentence, sometimes citing guidelines or minimums.",
    ],
    defense: [
      "Presents mitigating factors (background, remorse, rehabilitation).",
      "Files appeals for legal errors or challenges the sentence.",
      "Explores alternatives: probation, diversion, treatment programs.",
    ],
    questions: [
      "What sentencing range am I facing?",
      "What mitigating factors can we present?",
      "Do I have grounds for an appeal, and what's the deadline?",
    ],
    risks: [
      "Missing the strict appeal filing deadline.",
      "Not raising all mitigating factors at sentencing.",
    ],
  },
];

export const RIGHTS = [
  { title: "The Right to Remain Silent", body: "You do not have to answer questions from police. Clearly state: 'I am invoking my right to remain silent.' Then stop talking. Silence cannot be used as evidence of guilt.", statute: "5th Amendment" },
  { title: "The Right to an Attorney", body: "You have the right to a lawyer during questioning and at all critical stages. If you can't afford one, the court must appoint a public defender. Ask for a lawyer and stop answering questions.", statute: "6th Amendment" },
  { title: "Protection from Unreasonable Searches", body: "Police generally need a warrant or your consent to search you, your home, or your car. You can say: 'I do not consent to a search.' This does not make you guilty.", statute: "4th Amendment" },
  { title: "The Right to Know the Charges", body: "You must be told what you are accused of. You are entitled to a clear statement of the charges against you.", statute: "6th Amendment" },
  { title: "The Presumption of Innocence", body: "You are innocent until proven guilty. The prosecution must prove every element beyond a reasonable doubt. You do not have to prove your innocence.", statute: "Due Process" },
  { title: "The Right to a Speedy & Public Trial", body: "You have the right to a trial without unreasonable delay, by an impartial jury, in most criminal cases.", statute: "6th Amendment" },
  { title: "Protection Against Double Jeopardy", body: "You cannot be tried twice for the same offense after an acquittal or conviction for that offense.", statute: "5th Amendment" },
  { title: "Protection from Excessive Bail", body: "Bail cannot be set unreasonably high just to keep you detained. Your attorney can argue for release or lower bail.", statute: "8th Amendment" },
];

export const GLOSSARY = [
  { term: "Arraignment", def: "Your first court appearance where the charges are read and you enter a plea." },
  { term: "Bail", def: "Money or conditions set by the court to release you while ensuring you return for future dates." },
  { term: "Brady Material", def: "Evidence favorable to the defense that the prosecution is legally required to share." },
  { term: "Indictment", def: "A formal accusation by a grand jury that there is enough evidence to charge you with a crime." },
  { term: "Felony", def: "A serious crime usually punishable by more than one year in prison." },
  { term: "Misdemeanor", def: "A less serious crime usually punishable by up to one year in jail or fines." },
  { term: "Plea Bargain", def: "An agreement where you plead guilty to a lesser charge or for a lighter sentence." },
  { term: "Probable Cause", def: "A reasonable basis to believe a crime was committed — needed for arrests and searches." },
  { term: "Discovery", def: "The pre-trial process where both sides exchange evidence." },
  { term: "Mens Rea", def: "The 'guilty mind' — the intent or knowledge of wrongdoing needed for many crimes." },
  { term: "Acquittal", def: "A finding of not guilty; the defendant is legally cleared of the charge." },
  { term: "Arraignment Plea", def: "Your response to charges: guilty, not guilty, or no contest (nolo contendere)." },
  { term: "Bench Warrant", def: "An order from a judge for your arrest, often for missing a court date." },
  { term: "Bond", def: "A promise (often through a bondsman) to pay bail if you fail to appear." },
  { term: "Continuance", def: "A postponement of a court date to a later time." },
  { term: "Deposition", def: "Sworn out-of-court testimony recorded for later use." },
  { term: "Diversion Program", def: "An alternative to prosecution — completing requirements to get charges dismissed." },
  { term: "Exculpatory Evidence", def: "Evidence that tends to show you are not guilty." },
  { term: "Grand Jury", def: "A group of citizens who decide whether there's enough evidence to indict." },
  { term: "Hearsay", def: "An out-of-court statement offered for its truth; often inadmissible." },
  { term: "Miranda Rights", def: "The warnings police must give about your right to silence and an attorney." },
  { term: "Motion to Suppress", def: "A request to exclude illegally obtained evidence from trial." },
  { term: "No Contest (Nolo)", def: "Not admitting guilt but accepting the punishment as if guilty." },
  { term: "Parole", def: "Supervised release from prison before a full sentence is served." },
  { term: "Probation", def: "Court supervision in the community instead of, or after, jail time." },
  { term: "Public Defender", def: "A court-appointed attorney for those who cannot afford private counsel." },
  { term: "Restitution", def: "Payment ordered to compensate victims for losses." },
  { term: "Statute of Limitations", def: "The deadline for the state to file charges for an offense." },
  { term: "Subpoena", def: "A legal order to appear in court or produce documents." },
  { term: "Voir Dire", def: "The jury selection process where potential jurors are questioned." },
  { term: "Warrant", def: "A judge's authorization for an arrest or a search." },
  { term: "Beyond a Reasonable Doubt", def: "The high standard of proof the prosecution must meet to convict." },
  { term: "Double Jeopardy", def: "The rule against being tried twice for the same offense." },
  { term: "Expungement", def: "The legal process of sealing or erasing a criminal record." },
  { term: "Preliminary Hearing", def: "A hearing where a judge decides if there's enough evidence for trial." },
];

export const PRESET_PROMPTS = [
  "What happens at an arraignment, and how should I prepare?",
  "What is Brady material and why does it matter in discovery?",
  "How does a plea deal work, and what should I consider before accepting one?",
  "Explain the difference between a felony and a misdemeanor.",
  "What questions should I ask my defense attorney at our first meeting?",
  "What are my rights if the police want to search my car?",
];

export const LAWYER_TIPS = [
  { title: "Public Defender", body: "Free if you qualify financially. Experienced in local courts but often carry heavy caseloads. You do not choose which one you get.", icon: "Shield" },
  { title: "Private Attorney", body: "You choose them and they may have more time per case, but fees vary widely. Ask about experience with your specific charge.", icon: "Briefcase" },
  { title: "Questions to Ask Any Lawyer", body: "How many cases like mine have you handled? What are the likely outcomes? Who will actually work on my case? How do you communicate? What are all the costs?", icon: "MessageCircleQuestion" },
  { title: "Red Flags", body: "Guarantees of a specific outcome, pressure to decide fast, unclear fees, or poor communication are warning signs.", icon: "AlertTriangle" },
];

// name, abbr, speedy trial general note, bail note
export const STATES = [
  { name: "Federal (U.S.)", abbr: "US", speedy: "Speedy Trial Act: generally 70 days from indictment/appearance to trial.", bail: "Bail Reform Act governs release; detention possible for flight/danger.", court: "U.S. District Courts; Federal Rules of Criminal Procedure." },
  { name: "Alabama", abbr: "AL", speedy: "Right to speedy trial; no fixed statutory day-count.", bail: "Bail is a right for most non-capital offenses.", court: "Circuit & District Courts." },
  { name: "Alaska", abbr: "AK", speedy: "Rule 45: generally 120 days to trial.", bail: "Bail set by schedule; conditions for release.", court: "Superior & District Courts." },
  { name: "Arizona", abbr: "AZ", speedy: "Generally 150 days in custody / 180 days out (felony).", bail: "Most offenses bailable; some exceptions.", court: "Superior Courts." },
  { name: "Arkansas", abbr: "AR", speedy: "Generally 12 months (9 if incarcerated).", bail: "Bail available for most offenses.", court: "Circuit Courts." },
  { name: "California", abbr: "CA", speedy: "Felony: 60 days from arraignment; misdemeanor: 30/45 days.", bail: "Recent reforms favor release; risk assessments used.", court: "Superior Courts; Penal Code." },
  { name: "Colorado", abbr: "CO", speedy: "Generally 6 months from not-guilty plea.", bail: "Bond required; conditions apply.", court: "District & County Courts." },
  { name: "Connecticut", abbr: "CT", speedy: "Generally 8–12 months depending on custody.", bail: "Presumption of release for many offenses.", court: "Superior Court." },
  { name: "Delaware", abbr: "DE", speedy: "Constitutional speedy trial right; case-by-case.", bail: "Bail set based on offense and risk.", court: "Superior & Court of Common Pleas." },
  { name: "District of Columbia", abbr: "DC", speedy: "100-day rule for detained defendants.", bail: "Strong presumption of pretrial release.", court: "D.C. Superior Court." },
  { name: "Florida", abbr: "FL", speedy: "Misdemeanor: 90 days; Felony: 175 days.", bail: "Most offenses bailable; bond schedules used.", court: "Circuit & County Courts." },
  { name: "Georgia", abbr: "GA", speedy: "Demand-based; typically 2 court terms.", bail: "Bail available for most offenses.", court: "Superior & State Courts." },
  { name: "Hawaii", abbr: "HI", speedy: "Rule 48: generally 6 months.", bail: "Bail set by schedule and risk.", court: "Circuit & District Courts." },
  { name: "Idaho", abbr: "ID", speedy: "Generally 6 months from information/indictment.", bail: "Most offenses bailable.", court: "District Courts." },
  { name: "Illinois", abbr: "IL", speedy: "In custody: 120 days; on bail: 160 days.", bail: "Cash bail largely eliminated (SAFE-T Act).", court: "Circuit Courts." },
  { name: "Indiana", abbr: "IN", speedy: "Rule 4: 70 days if demanded in custody; 1 year otherwise.", bail: "Bail available for most offenses.", court: "Circuit & Superior Courts." },
  { name: "Iowa", abbr: "IA", speedy: "Trial within 90 days of indictment if not waived.", bail: "Bail set by schedule.", court: "District Courts." },
  { name: "Kansas", abbr: "KS", speedy: "In custody: 150 days; on bond: 180 days.", bail: "Most offenses bailable.", court: "District Courts." },
  { name: "Kentucky", abbr: "KY", speedy: "Constitutional right; case-by-case.", bail: "Bail available; risk assessment used.", court: "Circuit & District Courts." },
  { name: "Louisiana", abbr: "LA", speedy: "Felony: 2 years to trial; misdemeanor: 1 year.", bail: "Most offenses bailable.", court: "District Courts." },
  { name: "Maine", abbr: "ME", speedy: "Constitutional speedy trial right.", bail: "Presumption of release for many offenses.", court: "Superior & District Courts." },
  { name: "Maryland", abbr: "MD", speedy: "Hicks rule: generally 180 days.", bail: "Reforms reduce reliance on cash bail.", court: "Circuit & District Courts." },
  { name: "Massachusetts", abbr: "MA", speedy: "Rule 36: generally 12 months.", bail: "Presumption of release; dangerousness hearings possible.", court: "Superior & District Courts." },
  { name: "Michigan", abbr: "MI", speedy: "180-day rule for incarcerated defendants.", bail: "Most offenses bailable.", court: "Circuit & District Courts." },
  { name: "Minnesota", abbr: "MN", speedy: "Trial within 60 days of demand.", bail: "Bail available; conditions apply.", court: "District Courts." },
  { name: "Mississippi", abbr: "MS", speedy: "270 days from arraignment.", bail: "Most offenses bailable.", court: "Circuit Courts." },
  { name: "Missouri", abbr: "MO", speedy: "Constitutional right; 180 days if demanded in custody.", bail: "Reforms favor least-restrictive conditions.", court: "Circuit Courts." },
  { name: "Montana", abbr: "MT", speedy: "Generally within 6 months of demand.", bail: "Most offenses bailable.", court: "District Courts." },
  { name: "Nebraska", abbr: "NE", speedy: "Trial within 6 months.", bail: "Bail available for most offenses.", court: "District & County Courts." },
  { name: "Nevada", abbr: "NV", speedy: "60 days from arraignment if demanded.", bail: "Reforms require individualized bail.", court: "District & Justice Courts." },
  { name: "New Hampshire", abbr: "NH", speedy: "Constitutional speedy trial right.", bail: "Presumption of release; PR bail common.", court: "Superior & Circuit Courts." },
  { name: "New Jersey", abbr: "NJ", speedy: "Speedy trial deadlines under bail reform.", bail: "Cash bail largely eliminated; risk-based release.", court: "Superior Courts." },
  { name: "New Mexico", abbr: "NM", speedy: "Case management deadlines by court.", bail: "Wealth-based detention limited by rule.", court: "District & Magistrate Courts." },
  { name: "New York", abbr: "NY", speedy: "CPL 30.30: felony ~6 months; misdemeanor 60–90 days.", bail: "Cash bail eliminated for many offenses.", court: "Supreme, County & Local Courts." },
  { name: "North Carolina", abbr: "NC", speedy: "Constitutional right; case-by-case.", bail: "Most offenses bailable.", court: "Superior & District Courts." },
  { name: "North Dakota", abbr: "ND", speedy: "Trial within 90 days of demand.", bail: "Most offenses bailable.", court: "District Courts." },
  { name: "Ohio", abbr: "OH", speedy: "Felony: 270 days; triple-count if jailed.", bail: "Bail set by schedule; reforms ongoing.", court: "Courts of Common Pleas." },
  { name: "Oklahoma", abbr: "OK", speedy: "Constitutional right; 1 year benchmark.", bail: "Most offenses bailable.", court: "District Courts." },
  { name: "Oregon", abbr: "OR", speedy: "Reasonable time; 60 days in custody benchmark.", bail: "Security release / conditional release used.", court: "Circuit Courts." },
  { name: "Pennsylvania", abbr: "PA", speedy: "Rule 600: 365 days to trial.", bail: "Most offenses bailable.", court: "Courts of Common Pleas." },
  { name: "Rhode Island", abbr: "RI", speedy: "Constitutional speedy trial right.", bail: "Most offenses bailable.", court: "Superior & District Courts." },
  { name: "South Carolina", abbr: "SC", speedy: "Constitutional right; docket-driven.", bail: "Bond hearings within 24 hours.", court: "Circuit & Summary Courts." },
  { name: "South Dakota", abbr: "SD", speedy: "180 days from first appearance.", bail: "Most offenses bailable.", court: "Circuit Courts." },
  { name: "Tennessee", abbr: "TN", speedy: "Constitutional right; case-by-case.", bail: "Most offenses bailable.", court: "Criminal & General Sessions Courts." },
  { name: "Texas", abbr: "TX", speedy: "Constitutional right; no fixed day-count.", bail: "Most offenses bailable; PR bonds possible.", court: "District & County Courts; Penal Code." },
  { name: "Utah", abbr: "UT", speedy: "Trial within 30 days of demand (in custody).", bail: "Risk-based pretrial release.", court: "District & Justice Courts." },
  { name: "Vermont", abbr: "VT", speedy: "Case management deadlines by rule.", bail: "Strong presumption of release.", court: "Superior Court, Criminal Division." },
  { name: "Virginia", abbr: "VA", speedy: "In custody: 5 months; on bail: 9 months.", bail: "Presumption of bail for most offenses.", court: "Circuit & District Courts." },
  { name: "Washington", abbr: "WA", speedy: "In custody: 60 days; out: 90 days.", bail: "Least-restrictive conditions favored.", court: "Superior & District Courts." },
  { name: "West Virginia", abbr: "WV", speedy: "3-term rule for trial.", bail: "Most offenses bailable.", court: "Circuit & Magistrate Courts." },
  { name: "Wisconsin", abbr: "WI", speedy: "Misdemeanor: 60 days; felony: 90 days from demand.", bail: "Cash bail based on court appearance risk.", court: "Circuit Courts." },
  { name: "Wyoming", abbr: "WY", speedy: "Rule 48: 180 days from arraignment.", bail: "Most offenses bailable.", court: "District & Circuit Courts." },
];

const _future = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export const SAMPLE_CASE = {
  title: "State of New York v. Sample User",
  jurisdiction: "State",
  state: "New York",
  court: "Kings County Supreme Court",
  judge: "Hon. A. Rivera",
  case_number: "IND-2026-004821",
  lead_charge: "Grand Larceny (4th Degree)",
  charge_level: "Class E Felony",
  stage: "discovery",
  attorney: "Public Defender — Legal Aid Society",
  notes: "First offense. Gathering character references. Reviewing surveillance footage disclosed in discovery.",
  court_dates: [
    { id: "s1", label: "Discovery Conference", date: _future(21), notes: "Bring list of questions about the video evidence." },
    { id: "s2", label: "Motion Hearing", date: _future(45), notes: "Motion to suppress statement." },
  ],
  checklist: [
    { id: "c1", text: "Collect 3 character reference letters", done: true },
    { id: "c2", text: "Write down timeline of the night in question", done: true },
    { id: "c3", text: "Prepare questions for attorney about plea options", done: false },
    { id: "c4", text: "Confirm all release conditions are being met", done: false },
  ],
};

export const CHARGE_TYPES = [
  "DUI / DWI",
  "Drug possession",
  "Theft / larceny",
  "Assault / battery",
  "Domestic violence",
  "Weapons charge",
  "Fraud / white-collar",
  "Traffic / other misdemeanor",
  "Probation / parole violation",
  "Federal charge",
  "Other / not sure",
];

// 2025 HHS Federal Poverty Guidelines (annual). Used to estimate public defender eligibility.
export const FPL_2025 = {
  base: { first: 15650, add: 5500 }, // 48 contiguous states + DC
  Alaska: { first: 19550, add: 6880 },
  Hawaii: { first: 17990, add: 6330 },
};

export function fplAnnual(stateName, householdSize) {
  const t = FPL_2025[stateName] || FPL_2025.base;
  const n = Math.max(1, Number(householdSize) || 1);
  return t.first + t.add * (n - 1);
}

